/**
 * Leynix Workspace Configuration
 * 
 * Design Principles:
 * 1. 10-Second Rule: User understands where they are, what needs attention, and what to do next within 10 seconds
 * 2. One-Click Rule: Daily tasks are immediately accessible from Home
 * 3. Workspace Principle: Organized around work, not software modules
 * 
 * People ≠ Clients
 * - People = Your employees (caregivers, staff, team)
 * - Clients = People you serve (patients, residents, customers)
 */

import { 
  Home, 
  Users, 
  Briefcase, 
  DollarSign, 
  Building2, 
  Sparkles,
  Calendar,
  FileText,
  MessageSquare,
  UserPlus,
  ClipboardList,
  Shield,
  Settings,
  TrendingUp,
  Clock,
  MapPin,
  AlertCircle,
  GraduationCap,
  CreditCard,
  Receipt,
  BarChart3,
  FolderOpen,
  ScrollText,
  Palette,
  Database
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// WORKSPACE DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════════

export const WORKSPACES = {
  HOME: {
    id: 'home',
    name: 'Home',
    icon: Home,
    path: '/home',
    description: 'Your operational command center',
    subItems: [
      { name: "Today's Priorities", path: '/home', icon: AlertCircle },
      { name: 'Executive Snapshot', path: '/home/snapshot', icon: TrendingUp },
      { name: 'Quick Actions', path: '/home/actions', icon: Sparkles },
    ]
  },

  // CareHub - Prominent for pilot phase data entry
  CAREHUB: {
    id: 'carehub',
    name: 'CareHub',
    icon: Database,
    path: '/organization/data-entry',
    description: 'Add and manage caregivers & clients',
    highlight: true, // Special styling for pilot
    subItems: [
      { name: 'Add Caregiver', path: '/organization/data-entry?mode=caregiver', icon: UserPlus },
      { name: 'Add Client', path: '/organization/data-entry?mode=client', icon: Users },
      { name: 'New Client Intake', path: '/organization/data-entry?mode=intake', icon: ClipboardList },
    ]
  },
  
  PEOPLE: {
    id: 'people',
    name: 'People',
    icon: Users,
    path: '/people',
    description: 'Your team — employees, caregivers, and HR',
    subItems: [
      { name: 'All People', path: '/people', icon: Users },
      { name: 'Employees', path: '/people/employees', icon: Users },
      { name: 'Caregivers', path: '/people/caregivers', icon: Users },
      { name: 'Hiring', path: '/people/hiring', icon: UserPlus },
      { name: 'Training', path: '/people/training', icon: GraduationCap },
      { name: 'HR Dashboard', path: '/people/hr', icon: ClipboardList },
    ]
  },
  
  OPERATIONS: {
    id: 'operations',
    name: 'Operations',
    icon: Briefcase,
    path: '/operations',
    description: 'Clients, scheduling, visits, and coverage',
    subItems: [
      { name: 'Overview', path: '/operations', icon: Briefcase },
      { name: 'Clients', path: '/operations/clients', icon: Users },
      { name: 'Scheduling', path: '/operations/scheduling', icon: Calendar },
      { name: 'Visits', path: '/operations/visits', icon: MapPin },
      { name: 'Coverage', path: '/operations/coverage', icon: Clock },
      { name: 'Exceptions', path: '/operations/exceptions', icon: AlertCircle },
      { name: 'Communications', path: '/operations/communications', icon: MessageSquare },
    ]
  },
  
  BUSINESS: {
    id: 'business',
    name: 'Business',
    icon: DollarSign,
    path: '/business',
    description: 'Payroll, billing, and financials',
    subItems: [
      { name: 'Overview', path: '/business', icon: DollarSign },
      { name: 'Payroll', path: '/business/payroll', icon: CreditCard },
      { name: 'Billing', path: '/business/billing', icon: Receipt },
      { name: 'Reports', path: '/business/reports', icon: BarChart3 },
    ]
  },
  
  ORGANIZATION: {
    id: 'organization',
    name: 'Organization',
    icon: Building2,
    path: '/organization',
    description: 'Compliance, documents, policies, and settings',
    subItems: [
      { name: 'Overview', path: '/organization', icon: Building2 },
      { name: 'Compliance', path: '/organization/compliance', icon: Shield },
      { name: 'Documents', path: '/organization/documents', icon: FolderOpen },
      { name: 'Policies', path: '/organization/policies', icon: ScrollText },
      { name: 'Settings', path: '/organization/settings', icon: Settings },
      { name: 'Branding', path: '/organization/branding', icon: Palette },
    ]
  },
  
  ASK_LEYNIX: {
    id: 'ask-leynix',
    name: 'Ask Leynix',
    icon: Sparkles,
    path: '/ask',
    description: 'AI-powered operations assistant',
    isGlobal: true, // Always accessible via ⌘K
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// ROLE-BASED WORKSPACE ACCESS
// ═══════════════════════════════════════════════════════════════════════════════

export const ROLE_WORKSPACES = {
  // Executive / Owner — Full access
  platform_owner: ['HOME', 'CAREHUB', 'PEOPLE', 'OPERATIONS', 'BUSINESS', 'ORGANIZATION', 'ASK_LEYNIX'],
  agency_admin: ['HOME', 'CAREHUB', 'PEOPLE', 'OPERATIONS', 'BUSINESS', 'ORGANIZATION', 'ASK_LEYNIX'],
  owner: ['HOME', 'CAREHUB', 'PEOPLE', 'OPERATIONS', 'BUSINESS', 'ORGANIZATION', 'ASK_LEYNIX'],
  executive: ['HOME', 'CAREHUB', 'PEOPLE', 'OPERATIONS', 'BUSINESS', 'ORGANIZATION', 'ASK_LEYNIX'],
  
  // HR / Compliance
  hr_staff: ['HOME', 'CAREHUB', 'PEOPLE', 'ORGANIZATION', 'ASK_LEYNIX'],
  hr_compliance: ['HOME', 'CAREHUB', 'PEOPLE', 'ORGANIZATION', 'ASK_LEYNIX'],
  hr_manager: ['HOME', 'CAREHUB', 'PEOPLE', 'ORGANIZATION', 'ASK_LEYNIX'],
  
  // Operations
  operations: ['HOME', 'CAREHUB', 'PEOPLE', 'OPERATIONS', 'ASK_LEYNIX'],
  operations_manager: ['HOME', 'CAREHUB', 'PEOPLE', 'OPERATIONS', 'ASK_LEYNIX'],
  client_caregiver_services: ['HOME', 'CAREHUB', 'PEOPLE', 'OPERATIONS', 'ASK_LEYNIX'],
  scheduler: ['HOME', 'CAREHUB', 'PEOPLE', 'OPERATIONS', 'ASK_LEYNIX'],
  
  // Finance
  finance: ['HOME', 'CAREHUB', 'BUSINESS', 'ORGANIZATION', 'ASK_LEYNIX'],
  finance_manager: ['HOME', 'CAREHUB', 'BUSINESS', 'ORGANIZATION', 'ASK_LEYNIX'],
  payroll: ['HOME', 'CAREHUB', 'BUSINESS', 'ORGANIZATION', 'ASK_LEYNIX'],
  billing: ['HOME', 'CAREHUB', 'BUSINESS', 'ORGANIZATION', 'ASK_LEYNIX'],
  
  // Supervisor
  supervisor: ['HOME', 'CAREHUB', 'PEOPLE', 'OPERATIONS', 'ASK_LEYNIX'],
};

// ═══════════════════════════════════════════════════════════════════════════════
// CAREGIVER / EMPLOYEE WORKSPACE (Completely different experience)
// ═══════════════════════════════════════════════════════════════════════════════

export const CAREGIVER_WORKSPACES = {
  HOME: {
    id: 'home',
    name: 'Home',
    icon: Home,
    path: '/my-leynix',
    description: 'Your personal workspace',
  },
  MY_SCHEDULE: {
    id: 'schedule',
    name: 'My Schedule',
    icon: Calendar,
    path: '/my-leynix/schedule',
    description: 'Your upcoming visits',
  },
  MY_CLIENTS: {
    id: 'clients',
    name: 'My Clients',
    icon: Users,
    path: '/my-leynix/clients',
    description: 'People you care for',
  },
  MY_DOCUMENTS: {
    id: 'documents',
    name: 'My Documents',
    icon: FileText,
    path: '/my-leynix/documents',
    description: 'Your certifications and files',
  },
  MESSAGES: {
    id: 'messages',
    name: 'Messages',
    icon: MessageSquare,
    path: '/my-leynix/messages',
    description: 'Communications',
  },
  ASK_LEYNIX: {
    id: 'ask-leynix',
    name: 'Ask Leynix',
    icon: Sparkles,
    path: '/my-leynix/ask',
    description: 'Get help',
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get workspaces for a given role
 */
export function getWorkspacesForRole(role) {
  const normalizedRole = role?.toLowerCase();
  
  // Caregivers and employees get a completely different experience
  if (normalizedRole === 'caregiver' || normalizedRole === 'employee') {
    return Object.values(CAREGIVER_WORKSPACES);
  }
  
  // Get workspace keys for this role
  const workspaceKeys = ROLE_WORKSPACES[normalizedRole] || ROLE_WORKSPACES.platform_owner;
  
  // Return the actual workspace objects
  return workspaceKeys.map(key => WORKSPACES[key]).filter(Boolean);
}

/**
 * Check if a role has access to a specific workspace
 */
export function hasWorkspaceAccess(role, workspaceId) {
  const workspaces = getWorkspacesForRole(role);
  return workspaces.some(w => w.id === workspaceId);
}

/**
 * Get the default landing path for a role
 */
export function getDefaultPathForRole(role) {
  const normalizedRole = role?.toLowerCase();
  
  if (normalizedRole === 'caregiver' || normalizedRole === 'employee') {
    return '/my-leynix';
  }
  
  return '/home';
}

/**
 * Determine if user is a caregiver/employee (simplified experience)
 */
export function isCaregiverRole(role) {
  const normalizedRole = role?.toLowerCase();
  return normalizedRole === 'caregiver' || normalizedRole === 'employee';
}
